import { createContext, type ComponentChildren } from 'preact';
import { useCallback, useContext, useMemo, useRef, useState } from 'preact/hooks';
import type { ReaderAnchor } from '../utils/textWalker';

/** Public API consumed by surfaces, the toggle button, and the overlay. */
export interface ReaderModeApi {
  /** Reader Mode is currently visible (overlay + line shown). */
  active: boolean;
  /** Which surface the line is currently positioned over. */
  activeSurfaceId: string | null;
  /** The anchor under the line. null = not positioned yet. */
  anchor: ReaderAnchor | null;
  /** TTS playback state — 'playing' while reading aloud, 'idle' otherwise. */
  playState: 'idle' | 'playing';
  /** Toggle Reader Mode on the given surface. Calling toggle() on an already-
   *  active surface deactivates Reader Mode. Calling on a different surface
   *  switches the active surface. */
  toggle(surfaceId: string): void;
  /** Force Reader Mode off (e.g. surface unmounting, app navigating). */
  deactivate(): void;
  /** Move the line to a new anchor. */
  setAnchor(a: ReaderAnchor | null): void;
  /** Update TTS playback state (called by ReaderOverlay). */
  setPlayState(s: 'idle' | 'playing'): void;
  /** Surfaces register themselves so the overlay can resolve container refs
   *  by surfaceId. Returns a deregister fn for useEffect cleanup. */
  registerSurface(id: string, el: HTMLElement): () => void;
  /** Resolve the registered container for a surface, if any. */
  getSurface(id: string): HTMLElement | null;
}

const ReaderModeContext = createContext<ReaderModeApi | null>(null);

function saveAnchorToSession(surfaceId: string, a: ReaderAnchor) {
  try { sessionStorage.setItem(`reader-anchor-${surfaceId}`, JSON.stringify(a)); } catch {}
}

export function ReaderModeProvider({ children }: { children: ComponentChildren }) {
  const [active, setActive] = useState(false);
  const [activeSurfaceId, setActiveSurfaceId] = useState<string | null>(null);
  const [anchor, setAnchorState] = useState<ReaderAnchor | null>(null);
  const [playState, setPlayState] = useState<'idle' | 'playing'>('idle');

  // Surface registry. A ref (not state) — registering shouldn't re-render the
  // whole tree, only the overlay needs the lookup at drag-time.
  const surfacesRef = useRef<Map<string, HTMLElement>>(new Map());
  // Track current activeSurfaceId inside callbacks without stale closure issues.
  const activeSurfaceIdRef = useRef<string | null>(null);
  activeSurfaceIdRef.current = activeSurfaceId;

  const setAnchor = useCallback((a: ReaderAnchor | null) => {
    setAnchorState(a);
    if (a && activeSurfaceIdRef.current) saveAnchorToSession(activeSurfaceIdRef.current, a);
  }, []);

  // Always start with a null anchor on toggle-on so the overlay's init effect
  // re-anchors at the middle of the operator's CURRENT viewport. Restoring a
  // stored anchor caused the book to land off-screen when the operator had
  // scrolled away — making the toggle appear broken.
  const toggle = useCallback((surfaceId: string) => {
    setActive((prev) => {
      const switching = prev && activeSurfaceIdRef.current !== surfaceId;
      if (switching) {
        setActiveSurfaceId(surfaceId);
        setAnchorState(null);
        return true;
      }
      const next = !prev;
      if (next) {
        setActiveSurfaceId(surfaceId);
        setAnchorState(null);
      } else {
        setActiveSurfaceId(null);
        setAnchorState(null);
      }
      return next;
    });
  }, []);

  const deactivate = useCallback(() => {
    setActive(false);
    setActiveSurfaceId(null);
    setAnchorState(null);
    setPlayState('idle');
  }, []);

  const registerSurface = useCallback((id: string, el: HTMLElement) => {
    surfacesRef.current.set(id, el);
    return () => {
      const current = surfacesRef.current.get(id);
      if (current !== el) return;
      surfacesRef.current.delete(id);
      // If the active surface is the one going away (view switch, card collapse,
      // CodeViewer close), tear Reader Mode down. Otherwise the overlay keeps a
      // ref to a detached DOM node — getBoundingClientRect returns zeros, so
      // the cursor renders as an invisible strip at the top of the viewport.
      if (activeSurfaceIdRef.current === id) {
        setActive(false);
        setActiveSurfaceId(null);
        setAnchorState(null);
        setPlayState('idle');
      }
    };
  }, []);

  const getSurface = useCallback((id: string): HTMLElement | null => {
    return surfacesRef.current.get(id) ?? null;
  }, []);

  const api = useMemo<ReaderModeApi>(() => ({
    active,
    activeSurfaceId,
    anchor,
    playState,
    toggle,
    deactivate,
    setAnchor,
    setPlayState,
    registerSurface,
    getSurface,
  }), [active, activeSurfaceId, anchor, playState, toggle, deactivate, setAnchor, registerSurface, getSurface]);

  return (
    <ReaderModeContext.Provider value={api}>
      {children}
    </ReaderModeContext.Provider>
  );
}

/** Hook used by surfaces, the toggle, and the overlay.
 *  Throws if called outside `<ReaderModeProvider>` to surface integration bugs early. */
export function useReaderMode(): ReaderModeApi {
  const ctx = useContext(ReaderModeContext);
  if (!ctx) {
    throw new Error('useReaderMode() called outside <ReaderModeProvider>');
  }
  return ctx;
}

/** Same as useReaderMode but returns null when there is no provider — for
 *  components that may render in either mode (e.g. tests, isolated demos). */
export function useReaderModeOptional(): ReaderModeApi | null {
  return useContext(ReaderModeContext);
}
